import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Download, LogOut, Rocket, Settings2, Menu } from 'lucide-react';
import { useHashRouter } from './lib/useHashRouter';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider, useData } from './contexts/DataContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SettingsPanel from './components/shell/SettingsPanel';
import { preGameIntentsDB, miniBlocksDB, userSettingsDB, aiAnalysesDB } from './lib/supabase';
import { buildGameAnalysisPrompt, buildSessionAnalysisPrompt } from './lib/aiPrompts';
import { getPreQueueAnalytics, computeLpDeltasByGame, detectImportTriggers, isGameCancelled } from './lib/analytics';
import { generateDailyMission, isMissionForToday, buildMissionRecord } from './lib/dailyMission';
import Auth from './components/Auth';
import OnboardingModal from './components/OnboardingModal';
import GameList from './components/GameList';
import ExportTab from './components/ExportTab';
import SessionTab from './components/SessionTab';
import ChampionsTabContent from './components/ChampionsTabContent';
import GameFormModal from './components/GameFormModal';
import SessionCloseModal from './components/SessionCloseModal';
import AnalyticsTab from './components/AnalyticsTab';
import AIAnalysisModal from './components/AIAnalysisModal';
import PreGameModal from './components/PreGameModal';
import ObjectivesTab from './components/ObjectivesTab';
import RiotImportModal from './components/RiotImportModal';
import SeasonImportModal from './components/SeasonImportModal';
import ImportSuggestionsToast from './components/ImportSuggestionsToast';
import RankBadge from './components/RankBadge';
import DashboardV7 from './components/dashboard/DashboardV7';
import GlobalRails from './components/GlobalRails';
import AppHeaderLogo from './components/shell/AppHeaderLogo';
import AppFooter from './components/shell/AppFooter';
import AppBackground from './components/shell/AppBackground';
import LevelUpCelebration from './components/shell/LevelUpCelebration';
import DailyMissionCard from './components/shell/DailyMissionCard';
import DailyMissionSwapModal from './components/shell/DailyMissionSwapModal';
import TabsBar from './components/shell/TabsBar';
import CoachingTab from './components/CoachingTab';
import AutoImportIndicator from './components/AutoImportIndicator';
import { useAutoImport } from './lib/useAutoImport';
import { log } from './lib/logger';
import { buildTabsList } from './constants';
import SessionsManagerModal from './components/SessionsManagerModal';

// Phase 4 — hooks extraits
import { useGames } from './hooks/useGames';
import { useSessions } from './hooks/useSessions';
import { useRankSync } from './hooks/useRankSync';


// ============ MAIN APP ============
export default function AppRouter() {
  const { route, navigate } = useHashRouter();
  if (route === '/privacy') return <PrivacyPage onBack={() => navigate('/')} />;
  if (route === '/terms')   return <TermsPage   onBack={() => navigate('/')} />;
  return (
    <AuthProvider>
      <DataProvider>
        <SettingsProvider>
          <ADCTracker />
        </SettingsProvider>
      </DataProvider>
    </AuthProvider>
  );
}

function ADCTracker() {
  const { user, authLoading, signOut } = useAuth();
  const {
    games, setGames,
    sessions, setSessions,
    aiAnalyses, setAiAnalyses,
    miniBlocks, setMiniBlocks,
    pendingIntent, setPendingIntent,
    allIntents, setAllIntents,
    userSettings, setUserSettings,
    rankSnapshots, setRankSnapshots,
    loading,
  } = useData();
  const { champions, version, apexTiers } = useSettings();

  // ── UI state (local uniquement) ──────────────────────────────────────────
  const [showSettings, setShowSettings] = useState(false);
  const [importTriggers, setImportTriggers] = useState([]);
  const [activeFilter, setActiveFilter] = useState({ type: 'all', value: null });
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [showSessionClose, setShowSessionClose] = useState(false);
  const [showPreGame, setShowPreGame] = useState(false);
  const [showRiotImport, setShowRiotImport] = useState(false);
  const [showSeasonImport, setShowSeasonImport] = useState(false);
  const [showSessionsManager, setShowSessionsManager] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aiModalData, setAiModalData] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showSwapMission, setShowSwapMission] = useState(false);

  // ── Mission du jour (dérivée des miniBlocks) ─────────────────────────────
  const dailyMission = useMemo(() => {
    const todayMissions = miniBlocks.filter(b =>
      b.type === 'daily_mission' &&
      isMissionForToday(b) &&
      b.status !== 'expired' && b.status !== 'skipped'
    );
    if (todayMissions.length === 0) return null;
    return todayMissions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  }, [miniBlocks]);

  // ── Phase 4 : hooks extraits ─────────────────────────────────────────────

  const { addGame, importRiotGame, updateGame, deleteGame } = useGames({
    games, setGames,
    pendingIntent, setPendingIntent,
    setAllIntents,
    miniBlocks, setMiniBlocks,
    dailyMission,
    userSettings, setUserSettings,
    rankSnapshots, setRankSnapshots,
    editingGame, setEditingGame,
    setShowForm,
  });

  const { autoGroupSession, closeSession, deleteSession, updateSession } = useSessions({
    sessions, setSessions,
    setShowSessionClose,
  });

  const { refreshingRank, levelUpEvent, setLevelUpEvent, refreshRank } = useRankSync({
    userSettings, setUserSettings,
    rankSnapshots, setRankSnapshots,
  });

  // ── Auto-import Riot (15 min) ────────────────────────────────────────────
  const autoImport = useAutoImport({
    userSettings,
    existingGames: games,
    importGame: async (game) => {
      try {
        await importRiotGame(game);
      } catch (e) {
        if (!/déjà été importée/.test(e?.message || '')) {
          console.warn('[autoImport] importRiotGame failed:', e);
        }
      }
    },
    intervalMs: 15 * 60 * 1000,
    enabled: true
  });

  // ── Génération automatique mission du jour ───────────────────────────────
  const missionGenAttemptedRef = useRef(null);

  useEffect(() => {
    if (!user || loading) return;
    if (dailyMission) return;

    const todayKey = new Date().toISOString().slice(0, 10);
    const alreadyHandled = miniBlocks.some(b =>
      b.type === 'daily_mission' && b.data?.created_for_date === todayKey
    );
    if (alreadyHandled) return;

    if (missionGenAttemptedRef.current === todayKey) return;
    missionGenAttemptedRef.current = todayKey;

    (async () => {
      try {
        const newMission = generateDailyMission({
          games,
          championPool: userSettings?.champion_pool || []
        });
        if (!newMission) return;
        const record = buildMissionRecord(newMission);
        const saved = await miniBlocksDB.add(record);
        setMiniBlocks(prev => [saved, ...prev]);
      } catch (e) {
        console.error('[dailyMission] génération échouée (vérifie la migration SQL mini_blocks.data) :', e?.message || e);
      }
    })();
  }, [user, loading, dailyMission, miniBlocks, games, userSettings]);

  // ── Handlers pre-game intent ─────────────────────────────────────────────
  const savePreGameIntent = async (intent) => {
    const saved = await preGameIntentsDB.add(intent);
    setPendingIntent(saved);
    setAllIntents(prev => [saved, ...prev]);
    setShowPreGame(false);
  };

  // ── Handlers AI Analysis ─────────────────────────────────────────────────
  const openGameAIAnalysis = (game) => {
    const existing = aiAnalyses.find(a => a.game_id === game.id);
    const intent = pendingIntent?.matched_game_id === game.id ? pendingIntent : null;
    const prompt = buildGameAnalysisPrompt(game, games, sessions, miniBlocks, intent);
    setAiModalData({
      type: 'game',
      title: `Game #${game.gameNumber || games.findIndex(g => g.id === game.id) + 1} · ${game.champion} · ${game.result}`,
      prompt, existing,
      target: { game_id: game.id, analysis_type: 'game_review' }
    });
  };

  const openSessionAIAnalysis = (session) => {
    const sessionGames = games.filter(g => session.game_ids?.includes(g.id));
    const existing = aiAnalyses.find(a => a.session_id === session.id);
    const prompt = buildSessionAnalysisPrompt(session, sessionGames, games, miniBlocks);
    setAiModalData({
      type: 'session',
      title: `Session du ${new Date(session.closed_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}`,
      prompt, existing,
      target: { session_id: session.id, analysis_type: 'session_review' }
    });
  };

  const saveAIResponse = async (responseText) => {
    if (!aiModalData) return;
    if (aiModalData.existing) {
      await aiAnalysesDB.updateResponse(aiModalData.existing.id, responseText);
      setAiAnalyses(aiAnalyses.map(a =>
        a.id === aiModalData.existing.id ? { ...a, response_text: responseText } : a
      ));
    } else {
      const saved = await aiAnalysesDB.add({
        ...aiModalData.target,
        prompt_text: aiModalData.prompt,
        response_text: responseText
      });
      setAiAnalyses([saved, ...aiAnalyses]);
      setAiModalData({ ...aiModalData, existing: saved });
    }
  };

  // ── Handlers mini-blocs objectifs ────────────────────────────────────────
  const createMiniBlock = async (block) => {
    const saved = await miniBlocksDB.add(block);
    setMiniBlocks([saved, ...miniBlocks]);
  };

  const completeMiniBlock = async (id) => {
    await miniBlocksDB.complete(id);
    setMiniBlocks(miniBlocks.map(b =>
      b.id === id ? { ...b, status: 'completed', completed_at: new Date().toISOString() } : b
    ));
  };

  const abandonMiniBlock = async (id) => {
    if (!confirm('Abandonner ce mini-bloc ?')) return;
    await miniBlocksDB.abandon(id);
    setMiniBlocks(miniBlocks.map(b => b.id === id ? { ...b, status: 'abandoned' } : b));
  };

  const failMiniBlock = async (id) => {
    if (!confirm('Marquer ce bloc comme échec ?\nL\'objectif a été tenté mais non atteint.')) return;
    await miniBlocksDB.fail(id);
    setMiniBlocks(miniBlocks.map(b =>
      b.id === id ? { ...b, status: 'failed', completed_at: new Date().toISOString() } : b
    ));
  };

  const deleteMiniBlock = async (id) => {
    if (!confirm('Supprimer ce mini-bloc ?')) return;
    await miniBlocksDB.delete(id);
    setMiniBlocks(miniBlocks.filter(b => b.id !== id));
  };

  // ── Handlers blocs personnalisés ─────────────────────────────────────────
  const createCustomBlock = async (name) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      name: name.trim(),
      started_at_game_count: games.length,
      started_at_date: new Date().toISOString(),
      created_at: new Date().toISOString()
    };
    const blocks = [...(userSettings?.custom_blocks || []), newBlock];
    try {
      const updated = await userSettingsDB.upsert({ ...userSettings, custom_blocks: blocks });
      setUserSettings(updated);
      setActiveFilter({ type: 'block', value: newBlock.id });
      return newBlock;
    } catch (e) {
      alert('Erreur création bloc : ' + e.message);
      return null;
    }
  };

  const deleteCustomBlock = async (id) => {
    if (!confirm('Supprimer ce bloc ? Les games ne sont pas supprimées, juste le bloc.')) return;
    const blocks = (userSettings?.custom_blocks || []).filter(b => b.id !== id);
    try {
      const updated = await userSettingsDB.upsert({ ...userSettings, custom_blocks: blocks });
      setUserSettings(updated);
      if (activeFilter.type === 'block' && activeFilter.value === id) {
        setActiveFilter({ type: 'all', value: null });
      }
    } catch (e) {
      alert('Erreur suppression bloc : ' + e.message);
    }
  };

  // ── Champion pool ────────────────────────────────────────────────────────
  const updateChampionPool = async (newPool) => {
    try {
      const updated = await userSettingsDB.upsert({ ...userSettings, champion_pool: newPool });
      setUserSettings(updated);
    } catch (e) {
      alert('Erreur mise à jour pool : ' + e.message);
    }
  };

  // ── Objectif de saison ───────────────────────────────────────────────────
  const saveSeasonObjective = async (objective) => {
    try {
      const updated = await userSettingsDB.upsert({ ...userSettings, season_objective: objective });
      setUserSettings(updated);
    } catch (e) {
      console.error('[seasonObjective] sauvegarde échouée :', e?.message || e);
      const isSchemaErr = (e?.message || '').includes('season_objective') || (e?.message || '').includes('schema cache');
      alert(isSchemaErr
        ? "Impossible de sauver l'objectif : la base de données n'est pas à jour. Exécute la migration MIGRATION_V7.7_FIX.sql dans Supabase (SQL Editor)."
        : 'Erreur sauvegarde objectif de saison : ' + (e?.message || e));
    }
  };

  const handlePoolSuggestion = (sug) => {
    if (sug.type === 'new_unplayed_champion') {
      const trigger = {
        id: `new_champ_${sug.champion}`,
        severity: 'medium',
        title: `${sug.champion} ajouté à ton pool`,
        detail: `Tu n'as encore jamais joué ${sug.champion}. Voici un objectif d'apprentissage progressif pour découvrir le champion sans pression sur le winrate.`,
        suggestion: {
          title: `Apprendre ${sug.champion}`,
          description: `Objectif : 5 games avec ${sug.champion} pour découvrir les matchups, indépendamment du résultat.`,
          metric_type: 'games_played',
          metric_operator: '>=',
          metric_target: 5,
          target_games: 5,
          filter_champion: sug.champion
        }
      };
      setImportTriggers([trigger]);
    }
  };

  // ── Mission skip / swap ──────────────────────────────────────────────────
  const skipDailyMission = async () => {
    if (!dailyMission) return;
    try {
      await miniBlocksDB.update(dailyMission.id, { status: 'skipped' });
      setMiniBlocks(prev => prev.map(b =>
        b.id === dailyMission.id ? { ...b, status: 'skipped' } : b
      ));
    } catch (e) {
      console.error('[dailyMission] skip failed:', e);
    }
  };

  const swapDailyMission = async (newMissionData) => {
    if (!dailyMission) return;
    try {
      const todayKey = new Date().toISOString().slice(0, 10);
      const newData = {
        ...dailyMission.data,
        mission_type: newMissionData.type,
        label: newMissionData.label,
        description: newMissionData.description,
        target: newMissionData.target,
        reason: newMissionData.reason,
        progress: { gamesContributed: 0 },
        created_for_date: todayKey,
        auto_generated: false,
        swapped_from: dailyMission.data.mission_type
      };
      await miniBlocksDB.update(dailyMission.id, {
        status: 'active',
        target_games: newMissionData.target?.games || null,
        total_games: 0,
        data: newData
      });
      setMiniBlocks(prev => prev.map(b =>
        b.id === dailyMission.id
          ? { ...b, status: 'active', total_games: 0, data: newData }
          : b
      ));
      setShowSwapMission(false);
    } catch (e) {
      console.error('[dailyMission] swap failed:', e);
    }
  };

  // ── Dashboard actions ────────────────────────────────────────────────────
  const handleDashboardAction = (action, payload) => {
    switch (action) {
      case 'open_import':     setShowRiotImport(true); break;
      case 'view_analysis':   setActiveTab('analytics'); break;
      case 'view_lp_curve':   break;
      case 'view_block':
      case 'create_block':
      case 'edit_block':
      case 'retry_block':     setActiveTab('objectives'); break;
      case 'sideline_champion':
        if (payload?.champion) updateChampionPool(payload.champion, 'sidelined');
        break;
      case 'see_all_champions': setActiveTab('champions'); break;
      default: console.warn('Unhandled dashboard action:', action, payload);
    }
  };

  // ── Mémos ────────────────────────────────────────────────────────────────
  const filteredGames = useMemo(() => {
    let pool = games.filter(g => !isGameCancelled(g));
    if (activeFilter.type === 'all' || !activeFilter.value) return pool;
    if (activeFilter.type === 'patch') {
      return pool.filter(g => g.patch === activeFilter.value);
    }
    if (activeFilter.type === 'block') {
      const block = (userSettings?.custom_blocks || []).find(b => b.id === activeFilter.value);
      if (!block) return pool;
      return games.slice(block.started_at_game_count).filter(g => !isGameCancelled(g));
    }
    return pool;
  }, [games, activeFilter, userSettings]);

  const lpDeltasByGame = useMemo(() => computeLpDeltasByGame(games, rankSnapshots), [games, rankSnapshots]);
  const availablePatches = useMemo(() => {
    const set = new Set();
    games.forEach(g => { if (g.patch) set.add(g.patch); });
    return Array.from(set).sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }, [games]);
  const latestPatch = availablePatches[0] || null;

  const metrics = useMemo(() => {
    const total = filteredGames.length;
    if (total === 0) return { winrate: 0, avgDeaths: 0, cleanRatio: 0, blockProgress: 0 };
    const wins = filteredGames.filter(g => g.result === 'W').length;
    const totalDeaths = filteredGames.reduce((a, g) => a + g.deaths, 0);
    const cleanGames = filteredGames.filter(g =>
      !(g.deathDetails || []).some(d => d.type === 'greed')
    ).length;
    return {
      winrate: Math.round((wins / total) * 100),
      avgDeaths: +(totalDeaths / total).toFixed(1),
      cleanRatio: Math.round((cleanGames / total) * 100),
      blockProgress: total
    };
  }, [filteredGames]);

  const preQueueStats = useMemo(() => getPreQueueAnalytics(filteredGames, allIntents), [filteredGames, allIntents]);

  const currentSessionGames = useMemo(() => {
    const lastSessionDate = sessions.length > 0 ? new Date(sessions[0].closed_at) : new Date(0);
    return games.filter(g => new Date(g.timestamp) > lastSessionDate);
  }, [games, sessions]);

  // ── Guards ───────────────────────────────────────────────────────────────
  if (authLoading) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-zinc-500 font-mono text-sm tracking-wider">CHARGEMENT...</div>
    </div>
  );
  if (!user) return <Auth />;

  if (userSettings !== null && !userSettings?.onboarding_completed) {
    return (
      <OnboardingModal
        games={games}
        champions={champions}
        version={version}
        onComplete={async (config) => {
          await userSettingsDB.upsert(config);
          setUserSettings(prev => ({ ...prev, ...config }));
        }}
      />
    );
  }
  if (loading) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-zinc-500 font-mono text-sm tracking-wider">SYNCHRONISATION...</div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen text-white relative">
      <AppBackground userSettings={userSettings} />

      {/* Ambient effects */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 1 }}>
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-cyan-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-fuchsia-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>

      {/* Header */}
      <div className="relative border-b border-zinc-900 backdrop-blur-sm sticky top-0 bg-black/80 z-40">
        <div className="max-w-[1600px] mx-auto px-3 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
          <AppHeaderLogo />

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <AutoImportIndicator
              isImporting={autoImport.isImporting}
              lastImportAt={autoImport.lastImportAt}
              lastResult={autoImport.lastResult}
              onManualRefresh={autoImport.triggerImport}
              hasRiotId={!!userSettings?.riot_puuid}
            />
            <div className="h-8 w-px bg-zinc-800" />
            <button
              onClick={() => setShowRiotImport(true)}
              className="px-3 py-2 font-black tracking-wider transition flex items-center gap-2 text-xs border border-zinc-700 text-zinc-400 hover:border-fuchsia-400 hover:text-fuchsia-400"
              title="Importer depuis Riot API"
            >
              <Download size={14} /> IMPORT
            </button>
            <button
              onClick={() => setShowSeasonImport(true)}
              className="px-3 py-2 font-black tracking-wider transition flex items-center gap-2 text-xs border border-zinc-700 text-zinc-400 hover:border-cyan-400 hover:text-cyan-400"
              title="Importer toutes les games de la saison 2026"
            >
              <Download size={14} /> SAISON
            </button>
            <button
              onClick={() => setShowPreGame(true)}
              className={`px-3 py-2 font-black tracking-wider transition flex items-center gap-2 text-xs border ${pendingIntent ? 'border-cyan-400 text-cyan-400 bg-cyan-400/5' : 'border-zinc-700 text-zinc-400 hover:border-cyan-400 hover:text-cyan-400'}`}
              title={pendingIntent ? 'Intent en attente pour la prochaine game' : 'Pre-queue check'}
            >
              <Rocket size={14} /> {pendingIntent ? 'INTENT LOCKED' : 'PRE-QUEUE'}
            </button>
            <button onClick={signOut} className="text-zinc-500 hover:text-rose-400 transition" title="Déconnexion"><LogOut size={18} /></button>
            <button onClick={() => setShowSettings(true)} className="text-zinc-500 hover:text-zinc-300 transition" title="Paramètres"><Settings2 size={18} /></button>
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="border border-zinc-700 text-zinc-300 p-2 transition relative"
              aria-label="Menu"
            >
              <Menu size={18} />
              {pendingIntent && <span className="absolute top-0 right-0 w-2 h-2 bg-amber-400 rounded-full" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-zinc-950 border-b border-zinc-800 shadow-2xl">
            {userSettings?.current_tier && (
              <div className="p-3 border-b border-zinc-900">
                <RankBadge settings={userSettings} onRefresh={refreshRank} refreshing={refreshingRank} />
              </div>
            )}
            <div className="p-3 space-y-2">
              <button onClick={() => { setShowRiotImport(true); setMobileMenuOpen(false); }} className="w-full px-3 py-3 font-bold tracking-wider transition flex items-center gap-3 text-sm border border-zinc-700 text-zinc-300">
                <Download size={16} /> Import Riot API
              </button>
              <button onClick={() => { setShowSeasonImport(true); setMobileMenuOpen(false); }} className="w-full px-3 py-3 font-bold tracking-wider transition flex items-center gap-3 text-sm border border-zinc-700 text-zinc-300 hover:border-cyan-400 hover:text-cyan-400">
                <Download size={16} /> Import saison complète
              </button>
              <button onClick={() => { setShowPreGame(true); setMobileMenuOpen(false); }} className={`w-full px-3 py-3 font-bold tracking-wider transition flex items-center gap-3 text-sm border ${pendingIntent ? 'border-cyan-400 text-cyan-400 bg-cyan-400/5' : 'border-zinc-700 text-zinc-300'}`}>
                <Rocket size={16} /> {pendingIntent ? 'Intent locked' : 'Pre-queue check'}
              </button>
              <button onClick={() => { signOut(); setMobileMenuOpen(false); }} className="w-full px-3 py-3 font-bold tracking-wider transition flex items-center gap-3 text-sm border border-zinc-800 text-zinc-500 hover:text-rose-400 hover:border-rose-400">
                <LogOut size={16} /> Déconnexion
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs & content */}
      <div className="relative max-w-[1600px] mx-auto px-3 md:px-6 py-4 md:py-8">
        <TabsBar
          tabs={buildTabsList({
            sessionGamesCount: currentSessionGames.length,
            activeBlocksCount: miniBlocks.filter(b => b.status === 'active').length,
            gamesCount: games.length
          })}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        <GlobalRails
          activeTab={activeTab}
          games={filteredGames}
          allGames={games}
          userSettings={userSettings}
          rankSnapshots={rankSnapshots}
          seasonObjective={userSettings?.season_objective || null}
          onSaveSeasonObjective={saveSeasonObjective}
          apexTiers={apexTiers}
          dailyMission={dailyMission}
          activeFilter={activeFilter}
          onSetFilter={setActiveFilter}
          onSwapMission={() => setShowSwapMission(true)}
          onSkipMission={skipDailyMission}
        />

        {activeTab === 'dashboard' && (
          <DashboardV7
            games={filteredGames}
            miniBlocks={miniBlocks}
            rankSnapshots={rankSnapshots}
            lpDeltasByGame={lpDeltasByGame}
            userSettings={userSettings}
            version={version}
            importTriggers={importTriggers}
            onAction={handleDashboardAction}
            dailyMission={dailyMission}
            onSwapMission={() => setShowSwapMission(true)}
            onSkipMission={skipDailyMission}
            allGames={games}
            seasonObjective={userSettings?.season_objective || null}
            onSaveSeasonObjective={saveSeasonObjective}
            activeFilter={activeFilter}
            onSetFilter={setActiveFilter}
          />
        )}

        {activeTab === 'session' && (
          <SessionTab
            games={games}
            sessions={sessions}
            onCloseSession={() => setShowSessionClose(true)}
            onSessionAIAnalyze={openSessionAIAnalysis}
            onManageSessions={() => setShowSessionsManager(true)}
            aiAnalyses={aiAnalyses}
            champions={champions}
            version={version}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab games={filteredGames} sessions={sessions} rankSnapshots={rankSnapshots} userSettings={userSettings} champions={champions} version={version} />
        )}

        {activeTab === 'coaching' && (
          <CoachingTab games={games} userSettings={userSettings} />
        )}

        {activeTab === 'objectives' && (
          <ObjectivesTab
            games={games}
            miniBlocks={miniBlocks}
            onCreate={createMiniBlock}
            onComplete={completeMiniBlock}
            onAbandon={abandonMiniBlock}
            onFail={failMiniBlock}
            onDelete={deleteMiniBlock}
            champions={champions}
            version={version}
            championPool={userSettings?.champion_pool || []}
          />
        )}

        {activeTab === 'champions' && (
          <ChampionsTabContent
            games={games}
            filteredGames={filteredGames}
            champions={champions}
            version={version}
            userSettings={userSettings}
            updateChampionPool={updateChampionPool}
            handlePoolSuggestion={handlePoolSuggestion}
          />
        )}

        {activeTab === 'games' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
              <h2 className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-semibold">Historique Des Games</h2>
            </div>
            <GameList games={games} onDelete={deleteGame} onEdit={setEditingGame} onAIAnalyze={openGameAIAnalysis} aiAnalyses={aiAnalyses} userSettings={userSettings} champions={champions} version={version} lpDeltasByGame={lpDeltasByGame} />
          </div>
        )}

        {activeTab === 'export' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
              <h2 className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-semibold">Export Pour Analyse</h2>
            </div>
            <div className="mb-4 text-sm text-zinc-500">Copie le recap et colle-le dans Claude pour l'analyse. Le bouton "Backup JSON" sauvegarde toutes tes données.</div>
            <ExportTab games={games} />
          </div>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <SettingsPanel
          userEmail={user.email}
          userSettings={userSettings}
          onClose={() => setShowSettings(false)}
          onLogout={() => { setShowSettings(false); signOut(); }}
          onDeleted={() => setShowSettings(false)}
          onSaveRoles={async (roles) => {
            const updated = await userSettingsDB.upsert({ ...userSettings, ...roles });
            setUserSettings(updated);
          }}
        />
      )}

      <AppFooter userEmail={user.email} gamesCount={games.length} sessionsCount={sessions.length} />

      {/* Modals */}
      {showForm && (
        <GameFormModal
          onClose={() => setShowForm(false)}
          onSave={addGame}
          gameCount={games.length}
          champions={champions}
          version={version}
          latestPatch={latestPatch}
          userSettings={userSettings}
        />
      )}
      {editingGame && (
        <GameFormModal
          onClose={() => setEditingGame(null)}
          onSave={updateGame}
          gameCount={games.length}
          champions={champions}
          version={version}
          existingGame={editingGame}
          userSettings={userSettings}
        />
      )}
      {showSessionClose && (
        <SessionCloseModal
          games={currentSessionGames}
          onClose={() => setShowSessionClose(false)}
          onSave={closeSession}
        />
      )}
      {showPreGame && (
        <PreGameModal
          onClose={() => setShowPreGame(false)}
          onSave={savePreGameIntent}
          champions={champions}
          version={version}
        />
      )}

      <LevelUpCelebration event={levelUpEvent} onDismiss={() => setLevelUpEvent(null)} />

      {showSwapMission && dailyMission && (
        <DailyMissionSwapModal
          currentMission={dailyMission}
          onClose={() => setShowSwapMission(false)}
          onSelect={swapDailyMission}
        />
      )}

      {showRiotImport && (
        <RiotImportModal
          onClose={() => {
            setShowRiotImport(false);
            setTimeout(() => {
              const triggers = detectImportTriggers(games, miniBlocks, userSettings?.champion_pool);
              if (triggers.length > 0) setImportTriggers(triggers);
            }, 500);
          }}
          onImport={importRiotGame}
          existingGames={games}
          champions={champions}
          version={version}
        />
      )}

      {showSeasonImport && (
        <SeasonImportModal
          onClose={() => {
            setShowSeasonImport(false);
            setTimeout(() => {
              const triggers = detectImportTriggers(games, miniBlocks, userSettings?.champion_pool);
              if (triggers.length > 0) setImportTriggers(triggers);
            }, 500);
          }}
          puuid={userSettings?.riot_puuid}
          platform={userSettings?.riot_platform || 'europe'}
          existingGames={games}
          onGameImported={(savedGame) => {
            setGames(prev => [...prev, savedGame]);
          }}
        />
      )}

      {importTriggers.length > 0 && (
        <ImportSuggestionsToast
          triggers={importTriggers}
          onAccept={createMiniBlock}
          onDismiss={() => setImportTriggers([])}
          currentGameCount={games.length}
        />
      )}

      {showSessionsManager && (
        <SessionsManagerModal
          games={games}
          sessions={sessions}
          onClose={() => setShowSessionsManager(false)}
          onAutoGroup={autoGroupSession}
          onDeleteSession={deleteSession}
          onUpdateSession={updateSession}
        />
      )}

      {aiModalData && (
        <AIAnalysisModal
          title={aiModalData.title}
          prompt={aiModalData.prompt}
          existingAnalysis={aiModalData.existing}
          onClose={() => setAiModalData(null)}
          onSave={saveAIResponse}
        />
      )}
    </div>
  );
}
